import React, { useState, useRef, useEffect } from 'react';
import { Globe, ArrowRight, Home, Building2 } from 'lucide-react';

interface UrlInputPanelProps {
  onSubmit: (url: string) => void;
  onClose: () => void;
  recentUrls?: string[];
}

export const UrlInputPanel: React.FC<UrlInputPanelProps> = ({
  onSubmit,
  onClose,
  recentUrls = []
}) => {
  const [url, setUrl] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const normalizeUrl = (input: string): string => {
    const trimmed = input.trim();
    if (!trimmed) return '';

    if (trimmed.match(/^https?:\/\//i)) {
      return trimmed;
    }

    return `https://${trimmed}`;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const normalizedUrl = normalizeUrl(url);
    if (normalizedUrl) {
      onSubmit(normalizedUrl);
      setUrl('');
    }
  };

  const handleRecentUrlClick = (recentUrl: string) => {
    onSubmit(recentUrl);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <div className="fixed bottom-[90px] left-1/2 -translate-x-1/2 z-50 w-[640px]">
      <div className="bg-[#2C3442] rounded-lg shadow-xl border-2 border-[#DFE4EA]/20">
        {/* Header */}
        <div className="flex items-center gap-3 px-6 py-4 border-b border-[#DFE4EA]/10">
          <Globe size={24} className="text-[#DFE4EA]" />
          <span className="text-[#DFE4EA] font-medium text-lg">Add URL</span>
        </div>

        {/* URL Input */}
        <form onSubmit={handleSubmit} className="px-6 py-4">
          <div className="relative">
            <input
              ref={inputRef}
              type="text"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="https://www.example.com"
              className="w-full bg-transparent text-[#DFE4EA] text-base placeholder-[#DFE4EA]/40 border-b-2 border-[#DFE4EA]/30 focus:border-[#DFE4EA] outline-none pb-2 pr-10 transition-colors"
            />
            <button
              type="submit"
              className="absolute right-0 bottom-2 text-[#DFE4EA] hover:text-white transition-colors"
            >
              <ArrowRight size={24} />
            </button>
          </div>
        </form>

        {/* Recent URLs */}
        {recentUrls.length > 0 && (
          <div className="px-6 pb-4">
            <div className="text-[#DFE4EA]/60 text-sm mb-3">Recent</div>
            <div className="space-y-2">
              {recentUrls.slice(0, 2).map((recentUrl, index) => (
                <button
                  key={index}
                  onClick={() => handleRecentUrlClick(recentUrl)}
                  className="w-full flex items-center gap-3 text-left text-[#DFE4EA] hover:text-white transition-colors group"
                >
                  {index === 0 ?
                    <Home size={20} className="text-[#DFE4EA]/60 group-hover:text-[#DFE4EA]" /> :
                    <Building2 size={20} className="text-[#DFE4EA]/60 group-hover:text-[#DFE4EA]" />
                  }
                  <span className="text-sm truncate">{recentUrl}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
